import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const BookingCalendar = ({ bookings, onDateClick }) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Get first day of month and number of days
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Get bookings for a specific date
  const getBookingsForDate = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return bookings.filter(booking => {
      const checkin = new Date(booking.checkin).toISOString().split('T')[0];
      const checkout = new Date(booking.checkout).toISOString().split('T')[0];
      return dateStr >= checkin && dateStr < checkout;
    });
  };

  const previousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  // Create calendar grid
  const calendarDays = [];
  
  // Empty cells for days before month starts
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(<div key={`empty-${i}`} className="p-2 border border-gray-100"></div>);
  }

  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const dayBookings = getBookingsForDate(day);
    const isToday = new Date().toDateString() === new Date(year, month, day).toDateString();

    calendarDays.push(
      <div
        key={day}
        className={`p-2 border border-gray-200 min-h-[80px] cursor-pointer hover:bg-gray-50 transition ${
          isToday ? 'bg-blue-50 border-blue-300' : ''
        }`}
        onClick={() => onDateClick && onDateClick(new Date(year, month, day))}
      >
        <div className={`text-sm font-semibold mb-1 ${isToday ? 'text-blue-600' : 'text-gray-700'}`}>
          {day}
        </div>
        {dayBookings.length > 0 && (
          <div className="space-y-1">
            {dayBookings.slice(0, 2).map((booking, idx) => (
              <div
                key={idx}
                className={`text-xs px-1 py-0.5 rounded truncate ${
                  booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                  booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}
                title={`${booking.name} - ${booking.room}`}
              >
                {booking.name}
              </div>
            ))}
            {dayBookings.length > 2 && (
              <div className="text-xs text-gray-500">+{dayBookings.length - 2} more</div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <Card className="shadow-2xl border-2 border-[#3D2817]/20 mb-8">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-[#3D2817]">
            {monthNames[month]} {year}
          </h2>
          <div className="flex gap-2">
            <Button
              onClick={previousMonth}
              className="bg-[#3D2817] hover:bg-[#2D1F0F] text-white px-3 py-2"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Button
              onClick={nextMonth}
              className="bg-[#3D2817] hover:bg-[#2D1F0F] text-white px-3 py-2"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Day names */}
        <div className="grid grid-cols-7 gap-0 mb-2">
          {dayNames.map(day => (
            <div key={day} className="text-center text-sm font-semibold text-gray-600 p-2 bg-gray-100">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-0 border border-gray-300">
          {calendarDays}
        </div>

        {/* Legend */}
        <div className="flex gap-4 mt-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-100 border border-green-300"></div>
            <span>Confirmed</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-yellow-100 border border-yellow-300"></div>
            <span>Pending</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-red-100 border border-red-300"></div>
            <span>Cancelled</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BookingCalendar;
